/**
 * Landing page variant: C — Editorial / Tabloid
 * Converted from design prototype wireframe JSX.
 */
import { Link } from "react-router-dom";
import {
  WfFooter, FooterCTA, FaqRow,
  PlayBlob, ScriptLines,
} from "./primitives.tsx";

// Wireframe C — "Editorial / Tabloid"
// Newspaper-meets-zine. Big serif/marker masthead, columns, pullquotes, ticker, dateline.

export function WireframeC() {
  return (
    <div className="wf paper" style={{ maxWidth: 1280, width: "100%" }}>
      {/* MASTHEAD */}
      <div style={{ padding: "16px 60px 8px", borderBottom: "1px solid var(--rule-2)", display:"flex", justifyContent:"space-between", alignItems:"baseline" }}>
        <div className="label">VOL. III · NO. 47 · NICHE EDITION</div>
        <div className="label">FACELESS · DAILY</div>
        <div className="label">PRICE: 2 FREE CREDITS</div>
      </div>
      <div style={{ padding: "10px 60px 14px", borderBottom: "1px solid var(--rule-2)", textAlign:"center" }}>
        <div style={{ fontFamily:"Permanent Marker", fontSize: 88, letterSpacing: 4 }}>THE&nbsp;TUBEFORGE&nbsp;TIMES</div>
        <div className="hand" style={{ fontSize: 22, marginTop: -6 }}>"all the videos that fit, we make."</div>
      </div>

      {/* SECONDARY NAV — section runners */}
      <div className="row" style={{ borderBottom: "1px solid var(--rule-2)", padding: "8px 60px", justifyContent: "space-between" }}>
        {["FRONT PAGE","HOW IT WORKS","LIVE DESK","SHOWCASE","Q&A","SUBSCRIBE →"].map((s,i) =>
          <span key={s} className="label" style={{ color: i===5? "var(--accent)" : "var(--ink)" }}>{s}</span>
        )}
      </div>

      {/* FRONT PAGE — multi-column lede */}
      <section style={{ padding: "40px 60px 30px", display:"grid", gridTemplateColumns: "1.4fr 1fr 0.9fr", gap: 30, borderBottom: "1px solid var(--rule-2)" }}>
        {/* lede */}
        <div>
          <div className="label">FRONT PAGE · LEDE</div>
          <h1 style={{ fontFamily: "Permanent Marker", fontSize: 76, lineHeight: 0.92, marginTop: 8 }}>
            LOCAL CREATOR<br/>
            <span className="hl" style={{ background:"var(--highlight)", display:"inline-block", transform: "none" }}>SHIPS 4 VIDEOS</span><br />
            BEFORE COFFEE.
          </h1>

          {/* dateline + body */}
          <div className="type" style={{ columnCount: 2, columnGap: 22, marginTop: 22, fontSize: 16, lineHeight: 1.55 }}>
            <p><span style={{ fontFamily:"Permanent Marker", fontSize: 26 }}>T</span>UBEFORGE IS A SCRIPT-TO-MP4 PIPELINE for faceless YouTube channels. You pick a topic. The AI drafts a script. You red-pen the lines you don't like. You approve a storyboard of AI-generated frames, one segment at a time. You hit render. Nine-ish minutes later: a 1080p MP4 with voiceover, captions, and a Ken-Burns crawl over every frame.</p>
            <p>The whole thing is built around <em>two gates</em>. Nothing renders until you sign off on the words. Nothing renders until you sign off on the pictures. That's the part that doesn't feel like the rest of AI video. <span className="hl">Every other tool says "trust the model."</span> This one assumes you don't.</p>
          </div>

          <div className="row" style={{ marginTop: 22, gap: 14 }}>
            <Link to="/signup" className="btn accent" style={{ textDecoration: "none" }}>▶ Read the manual (free trial)</Link>
            <Link to="/pricing" className="btn ghost" style={{ textDecoration: "none" }}>subscribe / see plans</Link>
          </div>
        </div>

        {/* big hero image — "above the fold photo" */}
        <div>
          <div className="thumb" style={{ width: "100%", height: 320 }}>
            <div style={{ position:"absolute", inset:0, display:"flex", alignItems:"center", justifyContent:"center", zIndex:3 }}>
              <PlayBlob size={70} />
            </div>
          </div>
          <div className="label" style={{ marginTop: 8 }}>FIG. 1 — A FINISHED VIDEO, ABOUT TO PRESS PLAY.</div>
        </div>

        {/* right rail — sidebar */}
        <div className="col" style={{ gap: 18 }}>
          <div className="box flat" style={{ padding: 16, background: "var(--ink)", color:"var(--paper)" }}>
            <div className="label" style={{ color:"var(--accent)" }}>BY THE NUMBERS</div>
            <ul className="type" style={{ marginTop: 10, padding: 0, listStyle:"none", fontSize: 18, lineHeight: 1.6 }}>
              <li>1080p <span className="mute">renders, every plan</span></li>
            </ul>
          </div>
          <div className="box flat" style={{ padding: 16 }}>
            <div className="label">TODAY'S NICHES</div>
            <div className="row" style={{ flexWrap:"wrap", gap: 6, marginTop: 8 }}>
              {["Reddit","Top 10","History","Motivation","Stoicism","Crime","Conspiracy","Shorts"].map(t =>
                <span key={t} className="pill" style={{ fontSize: 11 }}>{t}</span>
              )}
            </div>
          </div>
          <div className="box flat" style={{ padding: 16 }}>
            <div className="label">ALSO INSIDE</div>
            <ul className="type" style={{ marginTop: 8, paddingLeft: 18, fontSize: 16, lineHeight: 1.5 }}>
              <li>How the two gates work — pg. 3</li>
              <li>Q&A: do I need a channel? — pg. 7</li>
            </ul>
          </div>
        </div>
      </section>


      {/* HOW IT WORKS */}
      <a id="how-it-works" />
      <section style={{ padding: "50px 60px", borderBottom: "1px solid var(--rule-2)" }}>
        <div className="row" style={{ alignItems:"baseline", justifyContent:"space-between" }}>
          <h2 style={{ fontSize: 52 }}>THE MANUAL · PG. 3</h2>
          <div className="label">FILED UNDER: HOW IT WORKS</div>
        </div>
        <div className="hand mute" style={{ fontSize: 22, marginTop: 4 }}>read it like a comic. start top-left.</div>

        <div style={{ display:"grid", gridTemplateColumns:"repeat(4, 1fr)", gap: 18, marginTop: 24 }}>
          {[
            ["I.","Pick the niche","You scroll a deck of presets. Reddit, Top-10, history, motivational, Shorts. Tap one. Done. Voice, image model, caption style — all set."],
            ["II.","Read the draft","AI hands you a hook, a body, a CTA. You read it like a copy editor. Cross out lines. Tighten. Regen the boring parts."],
            ["III.","Approve the frames","Each script segment gets an AI image. Twelve frames, eight frames, twenty. You stamp APPROVE on the good ones, REGEN on the rest."],
            ["IV.","Press render","Voiceover. Word-synced captions. Ken Burns. Out comes a 1080p MP4. Or it goes straight to YouTube. Your call."],
          ].map(([n,t,d]) => (
            <div key={n} style={{ position:"relative", paddingTop: 12 }}>
              <div style={{ fontFamily:"Permanent Marker", fontSize: 48, lineHeight: 0.9, color:"var(--accent)" }}>{n}</div>
              <div style={{ fontFamily:"Permanent Marker", fontSize: 26, marginTop: 8 }}>{t}</div>
              <div className="type" style={{ marginTop: 8, fontSize: 16, lineHeight: 1.5, columnCount: 1 }}>{d}</div>
              <div className="thumb" style={{ height: 110, marginTop: 14 }} />
            </div>
          ))}
        </div>
      </section>

      {/* LIVE DESK */}
      <section style={{ padding: "50px 60px", borderBottom: "1px solid var(--rule-2)", background: "var(--paper-2)" }}>
        <div className="label">LIVE DESK · PG. 4</div>
        <h2 style={{ fontSize: 56, marginTop: 6 }}>WATCH ONE GET MADE.</h2>
        <div className="hand mute" style={{ fontSize: 22 }}>tap any stage to scrub.</div>

        <div className="row" style={{ gap: 16, marginTop: 24, alignItems:"stretch" }}>
          {[
            { tag:"00:00 · TOPIC", body: <div className="hand" style={{ fontSize: 28, lineHeight: 1.05 }}>"the soviet space dogs"</div> },
            { tag:"00:05 · SCRIPT", body: <ScriptLines lines={6} /> },
            { tag:"00:25 · STORYBOARD", body:
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap: 6 }}>
                {Array.from({ length: 6 }).map((_,i) => <div key={i} className="thumb" style={{ height: 44 }} />)}
              </div>
            },
            { tag:"03:40 · RENDER", body:
              <div className="thumb" style={{ height: 130 }}>
                <div style={{ position:"absolute", inset:0, display:"flex", alignItems:"center", justifyContent:"center", zIndex:3 }}><PlayBlob size={42} /></div>
              </div>
            },
          ].map((c,i) => (
            <div key={i} className="box flat" style={{ flex: 1, padding: 14, background: "var(--paper)" }}>
              <div className="label" style={{ color: i===3 ? "var(--accent)" : "var(--ink-soft)" }}>{c.tag}</div>
              <div style={{ marginTop: 10 }}>{c.body}</div>
            </div>
          ))}
        </div>

        {/* timeline */}
        <div style={{ marginTop: 22, position:"relative", height: 26 }}>
          <div style={{ position:"absolute", inset:"12px 0 12px 0", borderTop:"2px dashed var(--ink)" }} />
          {[0,25,55,100].map((p,i) => (
            <div key={i} style={{ position:"absolute", left: `${p}%`, top: 0, transform:"translateX(-50%)", textAlign:"center" }}>
              <div style={{ width: 12, height: 12, background:"var(--ink)", margin:"7px auto", borderRadius: "50%" }} />
              <div className="label" style={{ fontSize: 10 }}>{["0:00","0:25","3:40","9:41"][i]}</div>
            </div>
          ))}
        </div>
      </section>


      {/* Q&A */}
      <a id="faq" />
      <section style={{ padding: "50px 60px", borderBottom: "1px solid var(--rule-2)" }}>
        <div className="label">Q&A · PG. 7</div>
        <h2 style={{ fontSize: 56, marginTop: 6 }}>LETTERS FROM READERS.</h2>
        <div style={{ marginTop: 20 }}>
          <FaqRow q="Q: How long does a single video really take, start to finish?" open />
          <FaqRow q="Q: Can I edit the script word-for-word, or just regenerate?" />
          <FaqRow q="Q: Do you do Shorts (vertical) too?" />
          <FaqRow q="Q: What happens if I hate the AI's images?" />
          <FaqRow q="Q: Can I publish straight to YouTube, or only download?" />
          <FaqRow q="Q: Failed render — do I lose credits?" />
        </div>
      </section>

      <FooterCTA headline="HOLD THE FRONT PAGE." sub="Your first 2 videos are on the house. No credit card. No commitment. Just the byline." />
      <WfFooter />
    </div>
  );
}
